import { ReactNode, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { Add16, Bin16 } from '@ingrid/iconography/react';
import { Button, ButtonLink, FormControl, IconButton, ListLayout, Modal, TextInput } from '@ingrid/luterkort-react';

import { useTranslation } from '~src/i18n/use-translation';

interface MetaEntry {
  value: string;
  key: string;
}

interface FormValues {
  meta: MetaEntry[];
}

interface Props {
  onValueChange: (data: FormValues['meta']) => void;
  value?: FormValues['meta'];
  trigger: ReactNode;
  title: string;
}

export function KeyValueFormDialog(props: Props) {
  const { title, trigger, value = [{ key: '', value: '' }], onValueChange } = props;

  const [isOpen, setIsOpen] = useState(false);

  const { control, handleSubmit, register, reset } = useForm<FormValues>({
    defaultValues: { meta: value },
  });

  const { fields, append, remove } = useFieldArray({ control: control, name: 'meta' });
  const { t } = useTranslation();

  function onSubmit(payload: FormValues) {
    onValueChange(payload.meta.filter(({ key, value }) => key && value));
    setIsOpen(false);
  }

  function handleOpenChange(isOpen: boolean) {
    setIsOpen(isOpen);

    if (!isOpen) {
      reset();
    }
  }

  function handleClear(index: number) {
    /**
     * If there's only one field left in the list, instead of removing it - we clear it.
     */
    if (fields.length === 1) {
      reset({ meta: [{ key: '', value: '' }] });
      return;
    }

    remove(index);
  }

  return (
    <FormControl.Root>
      <Modal.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Modal.Trigger>{trigger}</Modal.Trigger>

        <Modal.Content>
          <FormControl.Form onSubmit={handleSubmit(onSubmit)} style={{ display: 'none' }} />

          <Modal.Header title={title} />

          <Modal.Body>
            {fields.map((field, index) => (
              <ListLayout.Row key={field.id}>
                <TextInput {...register(`meta.${index}.key`)} placeholder={t('common.key')} />

                <TextInput {...register(`meta.${index}.value`)} placeholder={t('common.value')} />

                <IconButton
                  aria-label={t('common.removeItem', { item: `#${index}` })}
                  onClick={() => handleClear(index)}
                  style={{ flexShrink: '0' }}
                  variant="secondary"
                >
                  <Bin16 />
                </IconButton>
              </ListLayout.Row>
            ))}

            <ButtonLink onClick={() => append({ key: '', value: '' })}>
              <Add16 />
              {t('common.addItem')}
            </ButtonLink>
          </Modal.Body>

          <Modal.Footer>
            <Modal.CancelButton>{t('common.cancel')}</Modal.CancelButton>
            <FormControl.SubmitButton asChild>
              <Button>{t('common.save')}</Button>
            </FormControl.SubmitButton>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </FormControl.Root>
  );
}
